'use client';

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { vendorSettlementApi } from '../../services/vendorSettlementApi';
import { FileText } from 'lucide-react';
import toast from 'react-hot-toast';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
    confirmed: { label: 'Onaylandı', color: 'bg-blue-100 text-blue-700' },
    paid: { label: 'Ödendi', color: 'bg-green-100 text-green-700' },
};

export default function VendorSettlementList() {
    const navigate = useNavigate();
    const [settlements, setSettlements] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchSettlements();
    }, []);

    const fetchSettlements = async () => {
        try {
            setLoading(true);
            const res = await vendorSettlementApi.getAll({ page: 1, limit: 50 });
            setSettlements(res.settlements || []);
        } catch {
            toast.error('Mutabakatlar yüklenemedi');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (val: number) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(val || 0);
    const formatDate = (val: any) => {
        if (!val) return '-';
        const d = val._seconds ? new Date(val._seconds * 1000) : new Date(val);
        return d.toLocaleDateString('tr-TR');
    };

    return (
        <div className="p-6">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-6">
                <FileText size={24} /> Mutabakatlarım
            </h1>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : settlements.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">Henüz onaylanmış mutabakat bulunmuyor</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-600 font-medium">
                            <tr>
                                <th className="px-4 py-3 text-left">Dönem</th>
                                <th className="px-4 py-3 text-left">Anlaşma</th>
                                <th className="px-4 py-3 text-right">Ciro</th>
                                <th className="px-4 py-3 text-right">Giderler</th>
                                <th className="px-4 py-3 text-right">Payınız</th>
                                <th className="px-4 py-3 text-center">Sipariş</th>
                                <th className="px-4 py-3 text-center">Durum</th>
                                <th className="px-4 py-3 text-center">Ödeme</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {settlements.map((s) => {
                                const st = STATUS_LABELS[s.status] || STATUS_LABELS.confirmed;
                                return (
                                    <tr
                                        key={s.id}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={() => navigate(`/vendor/settlements/${s.id}`)}
                                    >
                                        <td className="px-4 py-3 font-medium">{formatDate(s.periodStart)} - {formatDate(s.periodEnd)}</td>
                                        <td className="px-4 py-3">
                                            <span className="text-xs px-2 py-0.5 rounded bg-gray-100">
                                                {s.agreementType === 'profit_share' ? 'Kâr Ortaklığı' : 'Ciro Komisyonu'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right">{formatCurrency(s.totalRevenue)}</td>
                                        <td className="px-4 py-3 text-right text-red-600">{formatCurrency(s.totalExpenses)}</td>
                                        <td className="px-4 py-3 text-right text-green-600 font-medium">{formatCurrency(s.vendorShare)}</td>
                                        <td className="px-4 py-3 text-center">{s.orderCount}</td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`text-xs px-2 py-1 rounded-full font-medium ${st.color}`}>{st.label}</span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            {s.status === 'paid'
                                                ? <span className="text-xs text-green-600 font-medium">{formatCurrency(s.paidAmount)}</span>
                                                : <span className="text-xs text-gray-400">Bekliyor</span>}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
